import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Flex, notification, Radio, Table, TableColumnsType, Input, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { uniqueId } from 'lodash';
import { getCarbonFootprint, actionCarbonFootprint } from '@/adapters/master';
import { IApiResponse, ICarbonFootprintRes } from '@/interfaces/master';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

type Mode = 'insert' | 'update';
type Category = 'fertilizer' | 'material' | 'chemical' | 'electric' | 'fuel';

const getErrorMessage = (r: unknown): string => {
  if (r instanceof Error) return r.message;
  if (typeof r === 'object' && r !== null && 'message' in r)
    return (r as { message: string }).message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const CarbonFootprintPage: React.FC = () => {
  const [tab, setTab] = useState<Category>('fertilizer');
  const [mode, setMode] = useState<Mode>('insert');
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);

  const { data, refetch, isFetching } = useQuery<ICarbonFootprintRes, Error>({
    queryKey: ['carbonfootprint-list'],
    queryFn: getCarbonFootprint,
    retry: 0,
  });

  useEffect(() => {
    refetch();
  }, [tab, mode, refetch]);

  useEffect(() => {
    const dataKey = `${tab}_${mode}` as keyof ICarbonFootprintRes;
    const list = data?.[dataKey] || [];
    setSelected(list.map((item) => ({ ...item, _key: uniqueId('cbf-') })));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, mode, data]);

  const keys = useMemo(() => {
    switch (tab) {
      case 'material':
        return {
          id: 'V_CBF_MAT_ID',
          itemAndName: ['V_CBF_MAT_ITEM', 'V_CBF_MAT_DESC'],
          farmer: 'V_CBF_MAT_USER',
          tumbol: 'V_CBF_MAT_TUMBOL',
          amphur: 'V_CBF_MAT_AMPHUR',
          province: 'V_CBF_MAT_PROVINCE',
        };
      case 'chemical':
        return {
          id: 'V_CBF_CHEM_ID',
          itemAndName: ['V_CBF_CHEM_ITEM', 'V_CBF_CHEM_DESC'],
          farmer: 'V_CBF_CHEM_USER',
          tumbol: 'V_CBF_CHEM_TUMBOL',
          amphur: 'V_CBF_CHEM_AMPHUR',
          province: 'V_CBF_CHEM_PROVINCE',
        };
      case 'electric':
        return {
          id: 'V_CBF_ELEC_ID',
          itemAndName: ['V_CBF_ELEC_ITEM', 'V_CBF_ELEC_DESC'],
          farmer: 'V_CBF_ELEC_USER',
          tumbol: 'V_CBF_ELEC_TUMBOL',
          amphur: 'V_CBF_ELEC_AMPHUR',
          province: 'V_CBF_ELEC_PROVINCE',
        };
      case 'fuel':
        return {
          id: 'V_CBF_FUEL_ID',
          itemAndName: ['V_CBF_FUEL_ITEM', 'V_CBF_FUEL_DESC'],
          farmer: 'V_CBF_FUEL_USER',
          tumbol: 'V_CBF_FUEL_TUMBOL',
          amphur: 'V_CBF_FUEL_AMPHUR',
          province: 'V_CBF_FUEL_PROVINCE',
        };
      default: // fertilizer
        return {
          id: 'V_CBF_FERT_ID',
          itemAndName: ['V_CBF_FERT_ITEM', 'V_CBF_FERT_DESC'],
          farmer: 'V_CBF_FERT_USER',
          tumbol: 'V_CBF_FERT_TUMBOL',
          amphur: 'V_CBF_FERT_AMPHUR',
          province: 'V_CBF_FERT_PROVINCE',
        };
    }
  }, [tab]);

  const notifyBatchErrors = (title: string, batchResults: IBatchRejection[], originalData: any[]) => {
    const errors: string[] = [];
    if (Array.isArray(batchResults)) {
      batchResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const failedId = originalData[index]?.[keys.itemAndName[0]] || `Index: ${index}`;
          errors.push(`${failedId}: ${result.reason?.errors?.errorMessage || 'ไม่ทราบสาเหตุ'}`);
        }
      });
    }
    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul style={{ paddingLeft: '15px', margin: 0, maxHeight: '250px', overflowY: 'auto' }}>
            {errors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        ),
        duration: 8,
      });
      return true;
    }
    return false;
  };

  const cbfMutation = useMutation({
    mutationFn: async ({ action, payload }: { action: 'Create' | 'Update' | 'Delete'; payload: any[] }) => {
      const categoryMap: Record<Category, 'Fertilizer' | 'Material' | 'Chemical' | 'Electric' | 'Fuel'> = {
        fertilizer: 'Fertilizer',
        material: 'Material',
        chemical: 'Chemical',
        electric: 'Electric',
        fuel: 'Fuel',
      };
      return (await actionCarbonFootprint(categoryMap[tab], action, payload)) as IApiResponse;
    },
    onSuccess: (res, variables) => {
      const isDelete = variables.action === 'Delete';
      const successMsg = isDelete ? 'ลบข้อมูลสำเร็จ' : (mode === 'insert' ? 'Sync ข้อมูลสำเร็จ' : 'แก้ไขข้อมูลสำเร็จ');
      const failTitle = isDelete ? 'ลบข้อมูลไม่สำเร็จ' : (mode === 'insert' ? 'Sync ข้อมูลไม่สำเร็จ' : 'แก้ไขข้อมูลไม่สำเร็จ');
      if (!notifyBatchErrors(failTitle, res.data as IBatchRejection[], variables.payload)) {
        notification.success({ message: successMsg });
      }
      refetch();
    },
    onError: (err) => notification.error({ message: getErrorMessage(err) }),
  });

  const getPlantDisplay = (record: any) => {
    const k1 = record[keys.itemAndName[0]] || '';
    const k2 = record[keys.itemAndName[1]] || '';
    return `${k1}, ${k2}`.trim() || '-';
  };

  const getColumnSearchProps = (dataIndex: string | string[]) => ({
    filters: Array.from(new Set(selected.map((item) => {
      if (Array.isArray(dataIndex)) return `${item[dataIndex[0]] || ''} ${item[dataIndex[1]] || ''}`.trim() || '-';
      return item[dataIndex] ? String(item[dataIndex]) : '-';
    }))).filter(Boolean).map((val) => ({ text: val, value: val })),
    filterSearch: true,
    onFilter: (value: any, record: any) => {
      let val = Array.isArray(dataIndex) ? `${record[dataIndex[0]] || ''} ${record[dataIndex[1]] || ''}`.trim() : String(record[dataIndex]);
      return val.toLowerCase().includes(String(value).toLowerCase());
    },
  });

  const columns: TableColumnsType<any> = useMemo(() => [
    { title: 'ลำดับ', width: 60, align: 'center', render: (_, __, i) => <b>{i + 1}</b> },
    { title: 'ID', dataIndex: keys.id, width: 100, sorter: (a, b) => String(a[keys.id] || '').localeCompare(String(b[keys.id] || '')), ...getColumnSearchProps(keys.id) },
    { title: 'สินค้า, ชื่อ', render: (_, record) => getPlantDisplay(record), sorter: (a, b) => getPlantDisplay(a).localeCompare(getPlantDisplay(b)), ...getColumnSearchProps(keys.itemAndName) },
    { title: 'เกษตรกร', dataIndex: keys.farmer, render: (v) => <b>{v ?? '-'}</b>, sorter: (a, b) => String(a[keys.farmer] || '').localeCompare(String(b[keys.farmer] || '')), ...getColumnSearchProps(keys.farmer) },
    { title: 'ตำบลที่ตั้งสวน', dataIndex: keys.tumbol, ...getColumnSearchProps(keys.tumbol) },
    { title: 'อำเภอที่ตั้งสวน', dataIndex: keys.amphur, ...getColumnSearchProps(keys.amphur) },
    { title: 'จังหวัดที่ตั้งสวน', dataIndex: keys.province, ...getColumnSearchProps(keys.province) },
  ], [tab, keys, selected]);

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lower = searchText.toLowerCase();
    return selected.filter((item: any) =>
      Object.values(item).some(val => val !== null && typeof val !== 'object' && String(val).toLowerCase().includes(lower)) ||
      getPlantDisplay(item).toLowerCase().includes(lower)
    );
  }, [selected, searchText, keys]);

  return (
    <div style={{ padding: '20px' }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Flex vertical gap={4}>
          <Radio.Group value={tab} onChange={(e) => setTab(e.target.value)} buttonStyle="solid">
            <Radio.Button value="fertilizer">Fertilizer (ปุ๋ย)</Radio.Button>
            <Radio.Button value="material">Material (วัสดุ)</Radio.Button>
            <Radio.Button value="chemical">Chemical (เคมี)</Radio.Button>
            <Radio.Button value="electric">Electric (ไฟฟ้า)</Radio.Button>
            <Radio.Button value="fuel">Fuel (เชื้อเพลิง)</Radio.Button>
          </Radio.Group>
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value as Mode)} size="small">
            <Radio.Button value="insert">รายการรอนำเข้า (Add)</Radio.Button>
            <Radio.Button value="update">รายการแก้ไข/ลบ (Edit/Delete)</Radio.Button>
          </Radio.Group>
        </Flex>

        <Flex gap={12} align="flex-end">
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>รีเฟรช</Button>
          <Input.Search placeholder="ค้นหา..." allowClear onSearch={setSearchText} onChange={(e) => setSearchText(e.target.value)} style={{ width: 300 }} />
          {mode === 'update' && (
            <Button danger disabled={!selectedRowKeys.length || cbfMutation.isPending} onClick={() => cbfMutation.mutate({ action: 'Delete', payload: selectedRows })}>ลบข้อมูล</Button>
          )}
          <Button type="primary" style={{ minWidth: 150, backgroundColor: '#52c41a', borderColor: '#52c41a' }} disabled={!selectedRowKeys.length || cbfMutation.isPending} onClick={() => cbfMutation.mutate({ action: mode === 'insert' ? 'Create' : 'Update', payload: selectedRows })}>
            {cbfMutation.isPending ? 'กำลังดำเนินการ...' : mode === 'insert' ? 'ส่งข้อมูลไป OTOD >' : 'แก้ไขข้อมูลที่ OTOD >'}
          </Button>
        </Flex>
      </Flex>

      <Table rowSelection={{ selectedRowKeys, onChange: (keys, rows) => { setSelectedRowKeys(keys); setSelectedRows(rows); } }} columns={columns} dataSource={filteredData} loading={isFetching} size="small" bordered rowKey="_key" pagination={false} scroll={{ x: 'max-content' }} sticky={{ offsetHeader: 0 }} />
    </div>
  );
};

export default CarbonFootprintPage;